/** UI review (Oct 2026): person profile drawer/page and per-channel defaults. */
import type { UUID } from './types';
import type { MyPermissions } from './permissions';
import { settingsSectionAvailable } from './modules';

export type SkillState = 'valid' | 'expiring' | 'expired';

/** GET /workspaces/:ws/people/:userId/profile. Sections the caller may not see are null. */
/** Top search bar people results (GET people/directory). */
export interface DirectoryPerson { user_id: string; display_name: string; job_title: string | null; position: string | null; building: string | null }

/**
 * t231u: GET people/status, the phone People list. One row per active member. `clock` is null when they aren't
 * clocked in or the viewer may not see it (same rule as the profile's clock); `clock_visible` says which.
 * `shift` is their current or next published shift (14 days), which every member can see on the rota.
 */
export interface PeopleStatusRow {
  user_id: string; display_name: string; job_title: string | null; position: string | null;
  primary_building: { id: string; name: string } | null; building_ids: string[]; department_ids: string[];
  /** t330u: every position they hold (the org's positions, archived left out). Older servers leave it out. */
  position_ids?: string[];
  clock_visible: boolean;
  clock: { state: 'in' | 'break'; since: string; building_id: string | null; building: string | null } | null;
  shift: { starts_at: string; ends_at: string; building_id: string | null; building: string | null } | null;
}

/** Fuzzy person match for the search bar: 0 = no match; higher is better. Prefix > word start > substring > in-order letters. */
export function personMatchScore(q: string, p: Pick<DirectoryPerson, 'display_name' | 'job_title' | 'position' | 'building'>): number {
  const t = q.trim().toLowerCase();
  if (!t) return 0;
  const name = p.display_name.toLowerCase();
  if (name.startsWith(t)) return 100 - name.length / 100;
  const words = name.split(/\s+/);
  if (words.some(w => w.startsWith(t))) return 80;
  if (name.includes(t)) return 60;
  const extra = [p.job_title, p.position, p.building].filter(Boolean).join(' ').toLowerCase();
  if (t.length >= 3 && extra.split(/\s+/).some(w => w.startsWith(t))) return 40;
  // Initials ("jb" → James Brown) and letters in order with small gaps ("jmes" → James).
  if (t.length >= 2 && words.map(w => w[0]).join('').startsWith(t)) return 50;
  if (t.length >= 3) {
    let i = 0, gaps = 0, last = -1;
    for (let k = 0; k < name.length && i < t.length; k++) if (name[k] === t[i]) { if (last >= 0 && k - last > 1) gaps += k - last - 1; last = k; i++; }
    if (i === t.length && gaps <= 2) return 30 - gaps;
  }
  return 0;
}

export interface PersonProfile {
  user_id: UUID;
  display_name: string;
  email: string | null;
  phone: string | null;
  job_title: string | null;
  timezone: string | null;
  /** t426u: their profile photo (a signed URL, a few hours), or null for initials. */
  avatar_url?: string | null;
  status_text: string | null;
  status_emoji: string | null;
  preferred_language: string | null;
  active: boolean;
  joined_at: string | null;
  is_external: boolean;
  is_me: boolean;
  buildings: { id: UUID; name: string; primary: boolean }[];
  teams: { id: UUID; name: string; kind: 'department' | 'group'; lead: boolean }[];
  positions: { id: UUID; name: string; color: string }[];
  skills: { id: UUID; name: string; expires_on: string | null; state: SkillState }[];
  employment: { type: string; contracted_hours_per_week: number | null; start_date: string | null; workforce_role: string; scheduled_minutes_week?: number } | null;
  kiosk: { has_pin: boolean; has_badge: boolean; locked: boolean } | null;
  driver: { is_external: boolean; company_name: string | null; licence_categories: string[]; licence_expires_on: string | null; cpc_expires_on: string | null; points: number; active: boolean } | null;
  roles: { name: string; scope: string }[] | null;
  kudos_90d: number;
  /**
   * At a glance (t138u). Today's (or the next) published shift: null when the viewer can't see their rota.
   * `shift` null with today non-null means nothing scheduled in the next 60 days.
   */
  today?: { working_today: boolean; shift: { starts_at: string; ends_at: string; local_date: string; building: string | null; position: string | null } | null;
    /** t231u: their next published shifts (up to 5, 14 days), the same ones anyone can see on the rota. */
    upcoming?: { starts_at: string; ends_at: string; local_date: string; building: string | null; building_id: string | null; position: string | null }[] } | null;
  /**
   * Clock-in status, only for themselves, people whose timesheets the viewer approves, and (t231u) while clocked in at
   * a building where the viewer may see who is in (buildings.view_shifts). `building` is where that clock-in was.
   */
  clock?: { state: 'in' | 'break' | 'out' | 'not_in'; at: string | null; late: boolean; building?: string | null; building_id?: string | null } | null;
  /** The viewer's own 1:1 DM with them (never anyone else's): last 3 messages. null on your own profile. */
  dm?: { conversation_id: string | null; messages: { id: string; mine: boolean; preview: string; created_at: string }[] } | null;
  can: { edit_employment: boolean; /** t128: admins or anyone who can edit their employment. */ edit_job_title: boolean; unlock_kiosk: boolean; /** t712u: Set PIN / Reset PIN on their Clock-in tab. */ manage_kiosk_pin?: boolean; manage_roles: boolean; manage_skills: boolean; see_pay: boolean;
    /** t231u: approves their timesheets (the sheet shows their recent time entries). */ view_timesheets?: boolean;
    /** t354u: sees their time off balance and absences. */ view_time_off?: boolean;
    /** t368u: opens their HR record. */ view_hr?: boolean;
    /** t473u: may create a task assigned to them (the New task rule for assignees); never on your own profile. */ assign_task?: boolean;
    /** t473u: sees their week on the rota (rota viewers over them); on your own profile, you're on the rota. */ view_rota?: boolean;
    /** t462u: the viewer is a workspace Owner: sees and changes "Require two-factor sign-in" for them (yourself too). */ manage_two_factor?: boolean;
    /** t471u: the viewer is an Owner and they are deactivated: "Delete permanently" shows. */ delete_permanently?: boolean;
    /** t513u: Edit details (name, sign-in email, phone): owners and admins; only an Owner edits an Owner. */ edit_details?: boolean };
}

/** Per-channel defaults set in Channel settings (collab.conversations.settings). Members' own choices win. */
export interface ChannelSettings {
  /** Translate posts for members who have a reading language set, even if they haven't turned on auto-translate. */
  auto_translate?: boolean;
  /** Threads start collapsed for members who haven't chosen a thread view in this channel. */
  threads_collapsed?: boolean;
  /** Pre-fill "Require acknowledgement" requests: due this many hours after posting (null = no due date). */
  ack_due_hours?: number | null;
  /** t136: who may forward messages out of this channel. Default everyone. */
  forwarding?: ForwardingPolicy;
}
export type ForwardingPolicy = 'everyone' | 'managers' | 'nobody';
/** Can this viewer forward out of a conversation? DMs, group chats and discussions always allow it; channels follow their setting. */
export function canForwardFrom(c: { kind: string; settings?: ChannelSettings | null; can_manage?: boolean }): boolean {
  if (c.kind !== 'channel' && c.kind !== 'room') return true;
  const p = c.settings?.forwarding ?? 'everyone';
  return p === 'everyone' || (p === 'managers' && Boolean(c.can_manage));
}

/** GET /workspaces/:ws/admin/integrations (UI review Medium #14). Never carries secrets. */
export interface IntegrationStatus {
  key: 'odoo' | 'maps' | 'translation' | 'assistant' | 'meeting_notes' | 'calls' | 'email' | 'push' | 'dvla';
  group: 'integrations' | 'ai';
  name: string;
  state: 'ok' | 'off' | 'error' | 'unknown';
  detail: string;
  last_activity: string | null;
  last_error: string | null;
  testable: boolean;
  /** Where its settings live in the app, if anywhere. */
  settings_path: string | null;
}
export interface IntegrationTest { ok: boolean; message: string; ms: number }

/** t473u: the person profile action that opens New task with them picked (shown when `can.assign_task`). */
export const ASSIGN_TASK_LABEL = 'Assign them a task';
/** t473u: show "Assign them a task" on this profile? The server decides (`can.assign_task`); never on your own. */
export function showAssignTask(p: Pick<PersonProfile, 'is_me' | 'active' | 'can'> | null | undefined): boolean {
  return Boolean(p && !p.is_me && p.active && p.can.assign_task);
}
/** t473u: who a New task opened from someone's profile is for: just them (you're not added). */
export function assignTaskPrefill(personId: string): { assignee_ids: string[] } {
  return { assignee_ids: [personId] };
}

/**
 * t567u: "Private (only you)" on New task only while the task is just for you: no one else assigned and no team.
 * Hidden when it's assigned to someone else (e.g. Assign them a task from a profile); back if they're removed.
 * The API refuses a private task with other people on it either way.
 */
export function canMakeTaskPrivate(f: { assignee_ids?: readonly string[] | null; team_ids?: readonly string[] | null }, me: string | null | undefined): boolean {
  return !(f.assignee_ids ?? []).some(u => u !== me) && !(f.team_ids ?? []).length;
}

/** t473u: "View rota" on someone's profile opens the rota for this week, filtered to just them; on your own, "My rota". */
export const VIEW_ROTA_LABEL = 'View rota';
export const MY_ROTA_LABEL = 'My rota';
export const SHOW_EVERYONE_LABEL = 'Show everyone';
/** The muted line over a rota filtered from a profile. */
export const rotaPersonLine = (name: string) => `Showing ${name} only`;
/** Show View rota / My rota on this profile? The server decides (`can.view_rota`). */
export function showViewRota(p: Pick<PersonProfile, 'active' | 'can'> | null | undefined): boolean {
  return Boolean(p && p.active && p.can.view_rota);
}
export const viewRotaLabel = (p: Pick<PersonProfile, 'is_me'>) => (p.is_me ? MY_ROTA_LABEL : VIEW_ROTA_LABEL);
/**
 * Where View rota goes (a shareable link): this week (no date), every building (`building=all`, so none of their
 * shifts is hidden; one building when that's all you schedule), Employee view, filtered by `person=<id>`.
 * Desktop: the schedule; phone web and iOS: the Rota tab. Your own: My shifts (desktop) or My shifts on the Rota tab.
 */
export function personRotaHref(p: Pick<PersonProfile, 'user_id' | 'is_me'>, surface: 'desktop' | 'phone'): string {
  if (p.is_me) return surface === 'desktop' ? '/workforce/my-shifts' : '/rota?view=mine';
  const q = new URLSearchParams(surface === 'desktop' ? { loc: 'all', person: p.user_id } : { view: 'rota', building: 'all', person: p.user_id });
  return `${surface === 'desktop' ? '/workforce/schedule' : '/rota'}?${q.toString()}`;
}

/**
 * t567u: "View all of <name>’s user details" at the bottom of the person sheet opens Settings › People › Users on
 * that person. Shown to whoever can open that page (the same permissions as the settings registry's Users section).
 */
export const USER_DETAILS_PERMS = ['admin.users.manage', 'admin.roles.manage', 'workforce.employees.edit', 'workforce.settings.manage'] as const;
export const userDetailsPath = (userId: string) => `/settings/people/users/${userId}`;
export const viewUserDetailsLabel = (name: string) => `View all of ${name.trim()}’s user details`;
export function canOpenUserDetails(mine: Pick<MyPermissions, 'workspace' | 'scoped' | 'modules_off'> | null | undefined): boolean {
  if (!mine || !settingsSectionAvailable(mine.modules_off, 'people/users')) return false;
  return USER_DETAILS_PERMS.some(p => mine.workspace.includes(p) || mine.scoped.some(s => s.permissions.includes(p)));
}
